import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, QrCode, Trash2, Image as ImageIcon, Shapes, Loader2, Square } from "lucide-react";
import { api, errMsg, clipUrl } from "@/lib/api";
import { useMeta } from "@/lib/meta";
import { uid, borderInset } from "@/lib/layout";

const r1 = (v) => Math.round(v * 10) / 10;

function place(product, design, ratio, side, small) {
  const W = product.width_mm, H = product.height_mm;
  const m = (product.config.margin_mm ?? 1) + borderInset(design.border) + 0.3;
  const round = product.shape === "round" || product.shape === "oval";
  let h = (round ? Math.min(W, H) * 0.42 : H - 2 * m) * (small ? 0.7 : 1);
  let w = h * ratio;
  const maxW = round ? Math.min(W, H) * 0.42 : W * 0.38;
  if (w > maxW) { w = maxW; h = w / ratio; }
  const x = round ? (W - w) / 2 : side === "right" ? W - m - w : m;
  return { x: r1(x), y: r1((H - h) / 2), w: r1(w), h: r1(h) };
}

export function GraphicsPanel({ product, design, setDesign, setSelected }) {
  const meta = useMeta();
  const cfg = product.config;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [mono, setMono] = useState(true);
  const [qr, setQr] = useState("https://www.halfastamps.it");
  const full = design.graphics.length >= cfg.max_graphics;
  const round = product.shape === "round" || product.shape === "oval";

  const addGraphic = (g, side = "left") => {
    if (full) return toast.error(`Massimo ${cfg.max_graphics} elementi grafici per questo timbro`);
    setDesign((d) => {
      const pos = place(product, d, g.ratio || 1, side, g.type !== "image");
      return { ...d, graphics: [...d.graphics, { id: uid(), ...g, ...pos }] };
    });
    setSelected({ kind: "graphic", i: design.graphics.length });
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/uploads/logo?mono=${mono}`, fd);
      addGraphic({ type: "image", src: data.path, ctype: data.content_type, ratio: data.ratio, name: f.name });
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const update = (i, p) => setDesign((d) => ({ ...d, graphics: d.graphics.map((g, k) => (k === i ? { ...g, ...p } : g)) }));
  const remove = (i) => { setSelected(null); setDesign((d) => ({ ...d, graphics: d.graphics.filter((_, k) => k !== i) })); };
  const section = "rounded-lg border border-slate-200 bg-white p-3 space-y-3";

  return (
    <div className="space-y-4" data-testid="graphics-panel">
      <h3 className="text-xl font-bold uppercase tracking-wide">Grafica</h3>
      {cfg.allow_logo && (
        <div className={section}>
          <div className="flex items-center gap-2 text-sm font-semibold"><ImageIcon className="w-4 h-4 text-brand-navy" /> Logo</div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={upload} data-testid="logo-file-input" />
          <button data-testid="upload-logo-btn" disabled={busy || full} onClick={() => fileRef.current?.click()}
            className="press w-full h-10 rounded-md bg-brand-navy text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica logo (PNG, JPG, SVG)
          </button>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={mono} onChange={(e) => setMono(e.target.checked)} data-testid="logo-mono-checkbox" className="accent-brand-navy" />
            Converti automaticamente in monocromatico (consigliato per timbri)
          </label>
        </div>
      )}
      {cfg.allow_clipart && (
        <div className={section}>
          <div className="flex items-center gap-2 text-sm font-semibold"><Shapes className="w-4 h-4 text-brand-navy" /> Clip art</div>
          <div className="grid grid-cols-6 gap-1.5">
            {(meta?.clipart || []).map((n) => (
              <button key={n} data-testid={`clipart-${n}`} title={n} disabled={full} onClick={() => addGraphic({ type: "clipart", name: n, ratio: 1 })}
                className="press aspect-square rounded border border-slate-200 bg-white hover:border-brand-navy hover:bg-brand-yellow/20 p-1.5 disabled:opacity-30 disabled:pointer-events-none">
                <img src={clipUrl(n)} alt={n} className="w-full h-full" />
              </button>
            ))}
          </div>
        </div>
      )}
      {cfg.allow_qr && (
        <div className={section}>
          <div className="flex items-center gap-2 text-sm font-semibold"><QrCode className="w-4 h-4 text-brand-navy" /> QR code</div>
          <div className="flex gap-2">
            <input data-testid="qr-input" value={qr} onChange={(e) => setQr(e.target.value)} placeholder="URL, telefono o testo"
              className="flex-1 min-w-0 h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
            <button data-testid="add-qr-btn" disabled={!qr.trim() || full} onClick={() => addGraphic({ type: "qr", data: qr.trim(), ratio: 1 }, "right")}
              className="press h-9 px-3 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold hover:bg-brand-yellowdark disabled:opacity-50">Aggiungi</button>
          </div>
        </div>
      )}
      {full && <p className="text-xs text-amber-700" data-testid="graphics-limit-msg">Limite di {cfg.max_graphics} elementi grafici raggiunto</p>}
      {design.graphics.length > 0 && (
        <div className="space-y-2" data-testid="graphics-list">
          {design.graphics.map((g, i) => (
            <div key={g.id} className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2" onClick={() => setSelected({ kind: "graphic", i })}>
              <span className="text-xs font-semibold w-16 truncate">{g.type === "image" ? "Logo" : g.type === "qr" ? "QR" : g.name}</span>
              <input type="range" min="2" max={Math.max(product.width_mm, product.height_mm)} step="0.5" value={g.w} data-testid={`graphic-size-${i}`}
                onChange={(e) => { const w = +e.target.value; update(i, { w, h: r1(w / (g.ratio || g.w / g.h)) }); }} className="flex-1 accent-brand-navy" />
              <span className="font-mono text-[10px] w-14 text-right">{g.w}×{g.h}</span>
              <button data-testid={`graphic-delete-${i}`} onClick={(e) => { e.stopPropagation(); remove(i); }} className="text-slate-400 hover:text-brand-navy"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {cfg.allow_border && (
        <div className={section}>
          <div className="flex items-center gap-2 text-sm font-semibold"><Square className="w-4 h-4 text-brand-navy" /> Bordo</div>
          <div className="flex flex-wrap gap-1.5">
            {[["none", "Nessuno"], ["single", "Singolo"], ["double", "Doppio"], ...(round ? [] : [["rounded", "Arrotondato"]])].map(([v, l]) => (
              <button key={v} data-testid={`border-${v}-btn`} onClick={() => setDesign((d) => ({ ...d, border: { ...d.border, style: v } }))}
                className={`press px-3 h-8 rounded text-xs border ${design.border.style === v ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-200"}`}>{l}</button>
            ))}
          </div>
          {design.border.style !== "none" && (
            <select data-testid="border-width-select" value={design.border.width_pt} onChange={(e) => setDesign((d) => ({ ...d, border: { ...d.border, width_pt: +e.target.value } }))}
              className="h-8 rounded border border-slate-200 bg-white text-xs px-2">
              {[0.5, 1, 1.5, 2].map((w) => <option key={w} value={w}>Spessore {w} pt</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
